/**
 * PATH: apps/quote/src/pages/builder/BuilderWorkspaceEditor.tsx
 * PURPOSE: Premium 3-pane Quote Builder workspace orchestrator
 * WHY: Provides Excel-like workspace with left sections nav, center grid, right context
 */

import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Layers, Plus } from 'lucide-react'
import { quoteApi, type QuoteDetail } from '@/lib/quoteApi'
import { useAuth } from '@quotech/auth'
import { saveLastOpenedQuote } from './BuilderLanding'
import { BuilderWorkspaceProvider, BuilderWorkspaceLayout, useWorkspace } from '@/components/builder/BuilderWorkspace'
import SectionsDrawer from '@/components/builder/SectionsDrawer'
import BuilderRightDrawer from '@/components/builder/BuilderRightDrawer'
import UnifiedBottomBar from '@/components/builder/UnifiedBottomBar'
import { useToast } from '@/components/builder/Toast'
import { BuilderPageSkeleton } from '@/components/builder/Skeleton'
import OtherChargesSection from '@/components/builder/OtherChargesSection'
import RightEdgeRail from '@/components/builder/RightEdgeRail'
import { AssumptionsSheet, SummarySheet, TermsSheet } from '@/components/builder/sheets'
import ScopeSheetGrid from '@/components/builder/ScopeSheetGrid'
import ScopeAgGridEditor from '@/components/builder/scope-ag-grid/ScopeAgGridEditor'
import { getEffectiveVisibleColumnIds } from '@/components/builder/scope-sheet-grid/visible-columns-helper'

/**
 * Excel-grade editor toggle. Defaults to ON (AG-Grid). Legacy hand-rolled
 * grid is reachable via:
 *   localStorage.setItem('qb_legacy_grid', '1')
 * (set in the browser console — no UI toggle in this MR; legacy is the
 * fallback for users who hit AG-Grid bugs and need to keep working.)
 */
function useExcelGridEnabled(): boolean {
  // useState-with-initialiser so SSR / first-render is consistent.
  const [enabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    try {
      return window.localStorage.getItem('qb_legacy_grid') !== '1'
    } catch {
      return true
    }
  })
  return enabled
}
import SetupHintBanner from '@/components/builder/SetupHintBanner'
import { useBuilderEditsBuffer } from '@/hooks/useBuilderEditsBuffer'
import WorkspaceHeader from './builder-workspace/WorkspaceHeader'
import JobQuoteBar from '@/components/builder/JobQuoteBar'
import { toNumber, SECTION_CREATION_DEBOUNCE_MS } from './builder-workspace/builder-helpers'
import { useQuoteTabStore } from '../../config/tabs'

function WorkspaceEditorInner({ quoteId }: { quoteId: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const updateActiveTab = useQuoteTabStore(s => s.updateActiveTab)
  const { user } = useAuth()
  const { activeSheet, activateSection, activateAssumptions, setActiveSheet, openRightDrawer, selection, selectItem, setSaveStatus, markSaved, incrementPendingChanges } = useWorkspace()

  const editsBuffer = useBuilderEditsBuffer({ quoteId, debounceMs: 400, onFlushStart: () => setSaveStatus('saving'), onFlushSuccess: () => markSaved(), onFlushError: (e) => { setSaveStatus('error'); toast.error('Save failed', `${e.length} cell(s) could not be saved.`) }, onPatchStaged: () => incrementPendingChanges() })
  const useExcelGrid = useExcelGridEnabled()

  const quoteQuery = useQuery({ queryKey: ['quote', 'builder', quoteId], queryFn: () => quoteApi.getQuote(quoteId) })
  const [localQuote, setLocalQuote] = useState<QuoteDetail | null>(null)
  const [phantomFocusId, setPhantomFocusId] = useState<string | null>(null)
  const [scrollToId, setScrollToId] = useState<string | null>(null)
  const [externalPaste, setExternalPaste] = useState<{ nonce: number; data: string[][] } | null>(null)
  const autoCreated = useRef(false), lastCreateTime = useRef(0), creating = useRef(false), savedRef = useRef(false)

  useEffect(() => { if (quoteQuery.data) setLocalQuote(quoteQuery.data) }, [quoteQuery.data])
  useEffect(() => {
    if (localQuote?.quote_number) {
      updateActiveTab(location.pathname, localQuote.quote_number)
    }
  }, [localQuote?.quote_number, location.pathname, updateActiveTab])
  useEffect(() => {
    if (localQuote && user?.organisation_id && !savedRef.current) {
      savedRef.current = true
      saveLastOpenedQuote({ quoteId: localQuote.id, quoteNumber: localQuote.quote_number, projectName: localQuote.project_name, clientName: localQuote.client_name, status: String(localQuote.status), openedAt: new Date().toISOString(), organisationId: user.organisation_id })
    }
  }, [localQuote, user?.organisation_id])

  const isEditable = !!localQuote?.is_editable

  // Section creation mutation with optimistic update
  const createSectionMut = useMutation({
    mutationFn: (data: { title: string; section_type?: string }) => quoteApi.createSection(quoteId, data),
    onMutate: () => { creating.current = true },
    onSuccess: (s) => {
      creating.current = false; lastCreateTime.current = Date.now()
      setLocalQuote(prev => { if (!prev) return prev; if (prev.sections?.some(x => x.id === s.id)) return prev; return { ...prev, sections: [...(prev.sections || []), { ...s, items: s.items || [] }] } })
      qc.setQueryData(['quote', 'builder', quoteId], (old: QuoteDetail | undefined) => { if (!old) return old; if (old.sections?.some(x => x.id === s.id)) return old; return { ...old, sections: [...(old.sections || []), { ...s, items: s.items || [] }] } })
      activateSection(s.id); setScrollToId(s.id); setPhantomFocusId(s.id); toast.success('Section created', `"${s.title}" added`)
    },
    onError: (e: any) => { creating.current = false; toast.error('Add Section failed', e?.message || 'Could not create section.') },
  })

  const handleAddSection = useCallback((title?: string) => {
    if (creating.current || Date.now() - lastCreateTime.current < SECTION_CREATION_DEBOUNCE_MS) return
    createSectionMut.mutate({ title: title || `Section ${(localQuote?.sections?.length || 0) + 1}`, section_type: 'standard' })
  }, [localQuote?.sections?.length, createSectionMut])

  // Keyboard shortcuts
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement; const tag = t?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return
      if (e.altKey && e.key.toLowerCase() === 's') { e.preventDefault(); handleAddSection() }
      if (e.altKey && e.key.toLowerCase() === 'r') { e.preventDefault(); const sid = (activeSheet.type === 'section' && activeSheet.sectionId) ? activeSheet.sectionId : localQuote?.sections?.[0]?.id; if (sid) setPhantomFocusId(sid) }
    }
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h)
  }, [handleAddSection, activeSheet, localQuote?.sections])

  const activeSection = useMemo(() => {
    if (activeSheet.type !== 'section') return null
    const secs = localQuote?.sections ?? []; if (!secs.length) return null
    return secs.find(s => s.id === activeSheet.sectionId) ?? secs.find(s => (s.items?.length ?? 0) > 0) ?? secs[0]
  }, [activeSheet, localQuote?.sections])

  useEffect(() => { if (activeSection && activeSheet.sectionId !== activeSection.id) activateSection(activeSection.id) }, [activeSection?.id, activeSheet.sectionId, activateSection])
  useEffect(() => { if (localQuote && localQuote.sections?.length === 0 && !autoCreated.current && isEditable && !creating.current) { autoCreated.current = true; handleAddSection('Section 1') } }, [localQuote, isEditable, handleAddSection])

  // All CRUD mutations
  const refetch = () => quoteQuery.refetch()
  const recalcMut = useMutation({ mutationFn: () => quoteApi.recalculateQuote(quoteId), onSuccess: (u) => { setLocalQuote(u); qc.setQueryData(['quote', 'builder', quoteId], u); markSaved(); toast.success('Recalculated') }, onError: () => toast.error('Recalculation failed') })
  const pdfMut = useMutation({ mutationFn: () => quoteApi.exportQuote(quoteId, 'pdf'), onSuccess: (b) => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `quote-${localQuote?.quote_number || quoteId}.pdf`; a.click(); URL.revokeObjectURL(u); toast.success('PDF exported') }, onError: () => toast.error('Export failed') })
  const excelMut = useMutation({ mutationFn: () => quoteApi.exportQuote(quoteId, 'excel'), onSuccess: (b) => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = `quote-${localQuote?.quote_number || quoteId}.xlsx`; a.click(); URL.revokeObjectURL(u); toast.success('Excel exported') }, onError: () => toast.error('Export failed') })
  const submitMut = useMutation({ mutationFn: () => quoteApi.submitForApproval(quoteId), onSuccess: (u) => { setLocalQuote(u); qc.setQueryData(['quote', 'builder', quoteId], u); toast.success('Submitted') }, onError: () => toast.error('Submission failed') })
  // Duplicate the whole quote: asRevision=true keeps the number + bumps the
  // version (parent_quote chain); false mints a fresh copy. Lands the user in
  // the new builder and refreshes the job tab strip.
  const duplicateMut = useMutation({
    mutationFn: (asRevision: boolean) => quoteApi.duplicateQuote(quoteId, asRevision),
    onSuccess: (created, asRevision) => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      if (localQuote?.job_id) qc.invalidateQueries({ queryKey: ['job-quotes', localQuote.job_id] })
      if (created?.id) { toast.success(asRevision ? 'New revision created' : 'Quote duplicated'); navigate(`/builder/${created.id}`) }
    },
    onError: () => toast.error('Duplicate failed'),
  })
  const updateSectionMut = useMutation({ mutationFn: (v: { sectionId: string; patch: Record<string, unknown> }) => quoteApi.updateSection(quoteId, v.sectionId, v.patch), onSuccess: () => { refetch(); markSaved() } })
  const deleteSectionMut = useMutation({ mutationFn: (id: string) => quoteApi.deleteSection(quoteId, id), onSuccess: refetch })
  const createItemMut = useMutation({ mutationFn: (v: { sectionId: string; data: any }) => quoteApi.createLineItem(quoteId, v.sectionId, v.data), onSuccess: () => { refetch(); markSaved() } })
  const updateItemMut = useMutation({ mutationFn: (v: { sectionId: string; itemId: string; patch: Record<string, unknown> }) => quoteApi.updateLineItem(quoteId, v.sectionId, v.itemId, v.patch), onMutate: () => { incrementPendingChanges(); setSaveStatus('saving') }, onSuccess: () => { refetch(); markSaved() }, onError: () => { setSaveStatus('error'); toast.error('Save failed') } })
  const deleteItemMut = useMutation({ mutationFn: (v: { sectionId: string; itemId: string }) => quoteApi.deleteLineItem(quoteId, v.sectionId, v.itemId), onSuccess: refetch })

  // Patch quote.config — used by inline column header menu (visible_columns
  // toggle) so estimators don't have to round-trip through the Setup tab.
  const updateConfigMut = useMutation({
    mutationFn: (patch: Record<string, unknown>) => {
      const cfg = (localQuote?.config ?? {}) as Record<string, unknown>
      return quoteApi.updateQuote(quoteId, { config: { ...cfg, ...patch } })
    },
    onSuccess: (u) => {
      if (u) setLocalQuote(u)
      qc.invalidateQueries({ queryKey: ['quote', 'builder', quoteId] })
      markSaved()
    },
    onError: () => toast.error('Config update failed'),
  })

  // Duplicate row — clones the existing item (description, qty, unit, pmpd,
  // chargeouts) and posts a fresh row in the same section. Server assigns a
  // new id + sort_order; the new row appears at the section's tail. Excel
  // parity for "Ctrl+D on a row" or right-click → Duplicate.
  const handleDuplicateItem = useCallback(
    (sectionId: string, itemId: string) => {
      const sec = localQuote?.sections?.find((s) => s.id === sectionId)
      const src = sec?.items?.find((i) => i.id === itemId)
      if (!src) return
      const N = (v: unknown): number => {
        const n = Number.parseFloat(String(v ?? 0))
        return Number.isFinite(n) ? n : 0
      }
      createItemMut.mutate({
        sectionId,
        data: {
          description: src.description || '',
          location: src.location || '',
          quantity: N(src.quantity),
          unit: src.unit || 'm2',
          productivity_pmpd: N(src.productivity_pmpd),
          labour_chargeout: src.labour_chargeout != null ? N(src.labour_chargeout) : null,
          material_chargeout: src.material_chargeout != null ? N(src.material_chargeout) : null,
          plant_chargeout: src.plant_chargeout != null ? N(src.plant_chargeout) : null,
          subcon_chargeout: src.subcon_chargeout != null ? N(src.subcon_chargeout) : null,
        },
      })
    },
    [localQuote, createItemMut],
  )

  // Insert a fresh empty row above/below the anchor. The backend auto-assigns
  // sort_order at the section tail, so for a true "above/below" placement we'd
  // need a follow-up reorder. For now we settle for "appended to section" with
  // a toast hint — this is still a single-click win vs the current "click +
  // button at end of section" workflow. TODO: reorder support — tracked in
  // Excel-parity follow-ups.
  const handleInsertEmptyRow = useCallback(
    (sectionId: string, _itemId: string) => {
      void _itemId
      createItemMut.mutate({
        sectionId,
        data: {
          description: '',
          quantity: 0,
          unit: 'm2',
          productivity_pmpd: 15,
        },
      })
    },
    [createItemMut],
  )
  const updateMatMut = useMutation({ mutationFn: (v: { sectionId: string; itemId: string; materialId: string; patch: Record<string, unknown> }) => quoteApi.updateMaterial(quoteId, v.sectionId, v.itemId, v.materialId, v.patch), onMutate: () => { incrementPendingChanges(); setSaveStatus('saving') }, onSuccess: () => { refetch(); markSaved() }, onError: () => { setSaveStatus('error'); toast.error('Material update failed') } })
  const addMatMut = useMutation({ mutationFn: (v: { sectionId: string; itemId: string; data: Record<string, unknown> }) => quoteApi.addMaterial(quoteId, v.sectionId, v.itemId, v.data), onMutate: () => { incrementPendingChanges(); setSaveStatus('saving') }, onSuccess: () => { refetch(); markSaved(); toast.success('Material added') }, onError: () => { setSaveStatus('error'); toast.error('Add material failed') } })
  const delMatMut = useMutation({ mutationFn: (v: { sectionId: string; itemId: string; materialId: string }) => quoteApi.deleteMaterial(quoteId, v.sectionId, v.itemId, v.materialId), onMutate: () => { incrementPendingChanges(); setSaveStatus('saving') }, onSuccess: () => { refetch(); markSaved(); toast.success('Material removed') }, onError: () => { setSaveStatus('error'); toast.error('Delete material failed') } })

  const totals = useMemo(() => ({ totalEx: toNumber(localQuote?.total_excl_gst), marginPercent: toNumber(localQuote?.overall_margin_percent), manDays: toNumber(localQuote?.total_labour_days), scopeSubtotal: toNumber(localQuote?.scope_subtotal), otherCharges: toNumber(localQuote?.other_charges_total), discount: toNumber(localQuote?.discount_amount), totalInc: toNumber(localQuote?.total_incl_gst) }), [localQuote])
  const selectedItem = useMemo(() => { if (!selection.sectionId || !selection.itemId) return null; return localQuote?.sections?.find(s => s.id === selection.sectionId)?.items?.find(i => i.id === selection.itemId) || null }, [selection, localQuote])

  if (quoteQuery.isLoading || !localQuote) return <BuilderPageSkeleton />

  return (
    <BuilderWorkspaceLayout
      header={<>
        <WorkspaceHeader quote={localQuote} totals={totals} onBack={() => navigate('/quotes')} onRefresh={() => refetch()} onExportPdf={() => pdfMut.mutate()} onExportExcel={() => excelMut.mutate()} onRecalculate={() => recalcMut.mutate()} onSubmit={() => submitMut.mutate()} onAttach={() => openRightDrawer('context')} onDuplicate={(asRevision) => duplicateMut.mutate(asRevision)} isRefreshing={quoteQuery.isFetching} isPdfExporting={pdfMut.isPending} isExcelExporting={excelMut.isPending} isRecalculating={recalcMut.isPending} isSubmitting={submitMut.isPending} isDuplicating={duplicateMut.isPending} />
        {localQuote.job_id ? <JobQuoteBar jobId={localQuote.job_id} currentQuoteId={localQuote.id} /> : null}
      </>}
      leftPanel={<SectionsDrawer sections={localQuote.sections || []} onAddSection={() => handleAddSection()} onRenameSection={(id, t) => updateSectionMut.mutate({ sectionId: id, patch: { title: t } })} onDeleteSection={id => deleteSectionMut.mutate(id)} onScrollToSection={id => { activateSection(id); setScrollToId(id) }} activeSectionId={activeSheet.type === 'section' ? activeSheet.sectionId : null} />}
      centerContent={<>
        {activeSheet.type === 'section' && (localQuote.sections?.length || 0) > 0 && (
          <SetupHintBanner
            quote={localQuote}
            totalLineItems={(localQuote.sections || []).reduce((acc, s) => acc + (s.items?.length || 0), 0)}
            onOpenSetup={activateAssumptions}
          />
        )}
        {activeSheet.type === 'section' && (localQuote.sections?.length || 0) > 0 && useExcelGrid && (
          <ScopeAgGridEditor
            sections={localQuote.sections || []}
            isEditable={isEditable}
            visibleOptionalIds={getEffectiveVisibleColumnIds(localQuote)}
            customColumns={
              ((localQuote.config as Record<string, unknown> | undefined)?.custom_columns as
                | import('@/components/builder/scope-sheet-grid/ScopeSheetGridTypes').CustomColumnDef[]
                | undefined) ?? []
            }
            configDefaults={{
              defaultProductivityPmpd: (localQuote.config as Record<string, unknown> | undefined)?.defaultProductivityPmpd as number | string | null | undefined,
              defaultUnit: (localQuote.config as Record<string, unknown> | undefined)?.defaultUnit as string | undefined,
            }}
            onAddItem={(sid, data) => createItemMut.mutate({ sectionId: sid, data })}
            onUpdateItem={(sid, iid, patch) => editsBuffer.stagePatch(sid, iid, patch)}
            onDeleteItem={(sid, iid) => deleteItemMut.mutate({ sectionId: sid, itemId: iid })}
            onAddSection={handleAddSection}
            onPasteWithCreate={(sid, items, data, row) => editsBuffer.stagePasteCreate(sid, items, data, row)}
          />
        )}
        {activeSheet.type === 'section' && (localQuote.sections?.length || 0) > 0 && !useExcelGrid && (
          <ScopeSheetGrid sections={localQuote.sections || []} isEditable={isEditable}
            onAddItem={(sid, data) => createItemMut.mutate({ sectionId: sid, data })}
            onUpdateItem={(sid, iid, patch) => editsBuffer.stagePatch(sid, iid, patch)}
            onDeleteItem={(sid, iid) => deleteItemMut.mutate({ sectionId: sid, itemId: iid })}
            onAddSection={handleAddSection} onRenameSection={(sid, t) => updateSectionMut.mutate({ sectionId: sid, patch: { title: t } })} onDeleteSection={sid => deleteSectionMut.mutate(sid)}
            onSelectItem={selectItem} selectedItemId={selection.itemId} isAddingSectionPending={createSectionMut.isPending}
            onPasteWithCreate={(sid, items, data, row) => editsBuffer.stagePasteCreate(sid, items, data, row)}
            requestedPhantomFocusSectionId={phantomFocusId} onRequestedPhantomFocusHandled={() => setPhantomFocusId(null)}
            requestedScrollToSectionId={scrollToId} onRequestedScrollToSectionHandled={() => setScrollToId(null)}
            externalPaste={externalPaste} onExternalPasteHandled={() => setExternalPaste(null)}
            configDefaults={{
              defaultProductivityPmpd: (localQuote.config as Record<string, unknown> | undefined)?.defaultProductivityPmpd as number | string | null | undefined,
              defaultUnit: (localQuote.config as Record<string, unknown> | undefined)?.defaultUnit as string | undefined,
            }}
            customColumns={
              ((localQuote.config as Record<string, unknown> | undefined)?.custom_columns as
                | import('@/components/builder/scope-sheet-grid/ScopeSheetGridTypes').CustomColumnDef[]
                | undefined) ?? []
            }
            visibleColumnIds={getEffectiveVisibleColumnIds(localQuote)}
            onUpdateConfig={(patch) => updateConfigMut.mutate(patch)}
            onDuplicateItem={handleDuplicateItem}
            onInsertRowAbove={handleInsertEmptyRow}
            onInsertRowBelow={handleInsertEmptyRow} />
        )}
        {activeSheet.type === 'section' && (localQuote.sections?.length || 0) === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Layers className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Start building your quote</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Add your first section to begin entering line items</p>
            <button onClick={() => handleAddSection('Section 1')} disabled={createSectionMut.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"><Plus className="w-4 h-4 inline mr-2" />Add Section</button>
          </div>
        )}
        {activeSheet.type === 'otherCharges' && <div className="p-4 min-h-full"><OtherChargesSection quoteId={quoteId} charges={localQuote.other_charges || []} isEditable={isEditable} /></div>}
        {activeSheet.type === 'assumptions' && <div className="p-4 min-h-full"><AssumptionsSheet quoteId={quoteId} quote={localQuote} isEditable={isEditable} /></div>}
        {activeSheet.type === 'summary' && <div className="p-4 min-h-full"><SummarySheet quoteId={quoteId} quote={localQuote} /></div>}
        {activeSheet.type === 'terms' && <div className="h-full"><TermsSheet quoteId={quoteId} quote={localQuote} isEditable={isEditable} /></div>}
      </>}
      rightDrawer={<BuilderRightDrawer quoteId={quoteId} quote={localQuote} selectedItem={selectedItem} selectedSectionId={selection.sectionId}
        onItemUpdated={refetch} onUpdateLineItem={(sid, iid, p) => updateItemMut.mutate({ sectionId: sid, itemId: iid, patch: p })}
        onApplyAISuggestion={s => { const fs = localQuote?.sections?.[0]; if (fs) createItemMut.mutate({ sectionId: fs.id, data: { description: (s as any).item_name || (s as any).description || '', quantity: 1, unit: (s as any).unit || 'm2', productivity_pmpd: 15 } }) }}
        onApplyApplication={(sid, iid, aid) => updateItemMut.mutate({ sectionId: sid, itemId: iid, patch: { application_id: aid } })}
        onJumpToRow={(sid, iid) => { selectItem(sid, iid); document.getElementById(`section-${sid}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }}
        onAddRowWithApplication={(sid, aid) => createItemMut.mutate({ sectionId: sid, data: { description: '', quantity: 0, unit: 'm2', productivity_pmpd: 15 } }, { onSuccess: (item: any) => { if (item?.id) updateItemMut.mutate({ sectionId: sid, itemId: item.id, patch: { application_id: aid } }) } })} />}
      rightRail={<RightEdgeRail />}
      bottomBar={<UnifiedBottomBar sections={localQuote.sections || []} isEditable={isEditable}
        onAddSection={() => handleAddSection()} onAddRow={() => { const sid = (activeSheet.type === 'section' && activeSheet.sectionId) ? activeSheet.sectionId : localQuote.sections?.[0]?.id; if (sid) setPhantomFocusId(sid) }}
        onPaste={() => { (async () => { try { if (activeSheet.type !== 'section') { const fid = localQuote.sections?.[0]?.id; if (fid) activateSection(fid) }; const t = await navigator.clipboard.readText(); if (!t.trimEnd()) { toast.error('Clipboard empty'); return }; setExternalPaste({ nonce: Date.now(), data: t.trimEnd().split(/\r?\n/).map(r => r.split('\t')) }) } catch { toast.error('Paste failed', 'Press Ctrl+V inside the grid.') } })() }}
        onImportExcel={() => {}} onRecalculate={() => recalcMut.mutate()} isRecalculating={recalcMut.isPending} isAddingSectionPending={createSectionMut.isPending}
        onRenameSection={(sid, t) => updateSectionMut.mutate({ sectionId: sid, patch: { title: t } })} onDeleteSection={sid => deleteSectionMut.mutate(sid)} totals={totals} />}
    />
  )
}

export default function BuilderWorkspaceEditor({ quoteId }: { quoteId: string }) {
  const quoteQuery = useQuery({ queryKey: ['quote', 'builder', quoteId], queryFn: () => quoteApi.getQuote(quoteId) })
  return (
    <BuilderWorkspaceProvider quote={quoteQuery.data || null} isEditable={!!quoteQuery.data?.is_editable}>
      <WorkspaceEditorInner quoteId={quoteId} />
    </BuilderWorkspaceProvider>
  )
}
